import React from "react";
import { Button, Container } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

export type ConcludeItem = string | { text: string; url?: string };

export type JobDetailItem = {
  upperTitle?: string; // tiêu đề viết hoa, ví dụ: "JOB DESCRIPTION"
  description?: string; // đoạn mô tả dài dạng paragraph
  listTitle?: string; // tiêu đề cho danh sách bullet
  list?: string[];
  conclude?: ConcludeItem[]; // danh sách bullet với optional URLs
};

export type JobListDataProps = {
  title: string;
  subTitle: string;
  items: JobDetailItem[];
  img?: string;
  cities: string;
  dept?: string;
};

// Helper function to render text with URLs as links
const renderTextWithLinks = (text: string) => {
  // Regular expression to match URLs
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = text.split(urlRegex);

  return parts.map((part, index) => {
    // Check if this part is a URL
    if (part.match(urlRegex)) {
      return (
        <a
          key={index}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary"
        >
          {part}
        </a>
      );
    }
    return part;
  });
};

function renderSectionContent(list?: string[]) {
  if (!list || list.length === 0) return null;



  return (
    <ul className="mt-3">
      {list.map((item, idx) => (
        <li
          key={idx}
          className="mb-2"
          style={{ fontSize: "16px", lineHeight: "1.6" }}
        >
          {renderTextWithLinks(item)}
        </li>
      ))}
    </ul>
  );
}

function renderConcludeContent(list?: ConcludeItem[]) {
  if (!list || list.length === 0) return null;

  return (
    <>
      {list.map((item, idx) => {
        // Handle string items
        if (typeof item === 'string') {
          return (
            <p
              key={idx}
              className="mb-2"
              style={{ fontSize: "16px", lineHeight: "1.6" }}
            >
              {renderTextWithLinks(item)}
            </p>
          );
        }

        // Handle object items with text and optional url
        const { text, url } = item;
        return (
          <p
            key={idx}
            className="mb-2"
            style={{ fontSize: "16px", lineHeight: "1.6" }}
          >
            {url ? (
              <a href={url} target="_blank" rel="noopener noreferrer" className="text-primary">
                {text}
              </a>
            ) : (
              text
            )}
          </p>
        );
      })}
    </>
  );
}

const Section = ({ title, subTitle, items, cities }: JobListDataProps) => {
  const navigate = useNavigate();
  return (
    <React.Fragment>
      <section
        id={`job-detail-section-${title.replace(/\s+/g, '-').toLowerCase()}`}
        className="position-relative document-section"
      >
        <Container>
          <div className="container py-5 position-relative" style={{ maxWidth: "900px" }}>
            <div
              className="position-absolute start-50 translate-middle-x w-100"
              style={{ maxWidth: "900px", zIndex: 1000, top: 0, pointerEvents: "none" }}
            >
              <div className="d-flex justify-content-end px-3" style={{ marginTop: "4rem", marginRight: "1rem" }}>
                <a
                  href="mailto:careers@geargames.com"
                  className="btn btn-primary fw-bold text-white border-0 px-4 py-2"
                  style={{ borderRadius: "50px", pointerEvents: "auto" }}
                >
                  APPLY
                </a>
              </div>
            </div>
            {/* TITLE */}
            <h1
              className="fw-bold text-primary text-center mb-4"
              style={{ fontSize: "40px" }}
            >
              {title}
            </h1>

            <h5 className="text-center text-uppercase fw-medium mb-5">
              {subTitle}
            </h5>

            <p className="text-left text-uppercase mb-4">
              <span className="text-primary fw-bold">Location: </span>
              <span className="text-dark">{cities.replaceAll(",", " | ")}</span>
            </p>

            {/* BODY */}
            {items.map((sec, idx) => (
              <div key={idx} className="mb-4">
                {sec.upperTitle && (
                  <h4 className="fw-bold text-primary mb-3">{sec.upperTitle}</h4>
                )}

                {sec.description && (
                  <p style={{ fontSize: "16px", lineHeight: "1.6" }}>
                    {sec.description}
                  </p>
                )}

                {/* render list title + list (only if list exists) */}
                {sec.list && sec.list.length > 0 && (
                  <>
                    {sec.listTitle && (
                      <p style={{ fontSize: "16px", lineHeight: "1.6" }}>
                        {sec.listTitle}
                      </p>
                    )}
                    {renderSectionContent(sec.list)}
                  </>
                )}

                {/* render conclude paragraphs (after list) */}
                {sec.conclude && sec.conclude.length > 0 && (
                  <>{renderConcludeContent(sec.conclude)}</>
                )}
              </div>
            ))}

            {/* FOOTER */}
            <div className="text-center mt-5">
              <Button
                variant="primary"
                className="fw-bold text-white border-0 p-2"
                onClick={() => navigate("/#jobs")}
              >
                Back to careers
              </Button>
            </div>
          </div>
        </Container>
      </section>
    </React.Fragment>
  );
};

export default Section;
